/**
 * A69 officer two-factor sign-in: the pure policy (no I/O), shared by the edge middleware, the /mfa challenge
 * page, the profile enrolment section and destination sanitisation on /login.
 *
 * Two-factor is Supabase Auth's native TOTP MFA: enrolment (mfa.enroll -> challenge -> verify) writes a VERIFIED
 * row in auth.mfa_factors, and a successful challenge raises the session to AAL2 (the access token's `aal`
 * claim). The server-side rule requires AAL2 for the officer tier, behind a switch that defaults OFF so the
 * rollout is staged: enrolment is available to everyone first, enforcement is turned on later.
 *
 * Importable from client and edge code: no server-only dependencies.
 */
import { legacyTierForSlug } from '@/lib/auth/role-slugs'

/** Runtime switch read by the middleware. Exactly "true" (case/space-insensitive) turns enforcement ON. */
export const MFA_ENFORCE_ENV = 'MFA_REQUIRE_OFFICER_AAL2'
export const MFA_CHALLENGE_PATH = '/mfa'
export const MFA_ENROL_PATH = '/profile'
export const DEFAULT_DESTINATION = '/dashboard'

export type Aal = 'aal1' | 'aal2'
export type FactorLike = { id?: string; factor_type?: string; status?: string } | null | undefined

export function isOfficerAal2Enforced(env: Record<string, string | undefined>): boolean {
  return (env[MFA_ENFORCE_ENV] ?? '').trim().toLowerCase() === 'true'
}

/** The officer tier (officer and the DB slugs mapped to it) must hold AAL2 when enforcement is on. */
export function isMfaRequiredRole(role: string | null | undefined): boolean {
  if (!role) return false
  return legacyTierForSlug(role) === 'officer'
}

function isVerified(f: FactorLike): boolean {
  return !!f && f.status === 'verified'
}

/**
 * A verified TOTP factor: the only factor kind this app can challenge (MfaChallenge) and enrol (MfaEnrollment).
 * A verified factor of another kind (phone) does NOT count, so it never routes a user to a challenge they cannot
 * pass, and never hides the TOTP enrolment control. (Phone MFA is disabled on our GoTrue: no
 * GOTRUE_MFA_PHONE_* is set, so such a factor cannot be enrolled here; this keeps the app consistent anyway.)
 */
export function hasVerifiedTotpFactor(factors: readonly FactorLike[] | null | undefined): boolean {
  return pickVerifiedTotpFactor(factors) !== null
}

export function pickVerifiedTotpFactor<F extends FactorLike>(factors: readonly F[] | null | undefined): NonNullable<F> | null {
  if (!Array.isArray(factors)) return null
  const hit = factors.find((f) => isVerified(f) && f!.factor_type === 'totp')
  return (hit ?? null) as NonNullable<F> | null
}

/**
 * The `aal` claim of an access token. The token is only DECODED here: callers must use it only after the token
 * was verified by GoTrue (the middleware calls this solely when supabase.auth.getUser() succeeded).
 */
export function aalFromAccessToken(token: string | null | undefined): Aal | null {
  if (typeof token !== 'string') return null
  const parts = token.split('.')
  if (parts.length !== 3) return null
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
    const json = typeof atob === 'function'
      ? atob(padded)
      : Buffer.from(padded, 'base64').toString('binary')
    const payload = JSON.parse(json) as { aal?: unknown }
    return payload.aal === 'aal1' || payload.aal === 'aal2' ? payload.aal : null
  } catch {
    return null
  }
}

const SAME_ORIGIN_PROBE = 'https://same-origin.invalid'

/**
 * A same-origin relative destination (path + query + hash), or `fallback`. Refuses absolute and
 * protocol-relative URLs, backslashes, control characters and whitespace (browsers strip tab/newline, turning
 * "/\t/evil" into "//evil"), and the challenge page itself (no loop).
 */
export function safeRelativeDestination(raw: string | null | undefined, fallback: string = DEFAULT_DESTINATION): string {
  if (typeof raw !== 'string' || raw.length === 0) return fallback
  if (!raw.startsWith('/') || raw.startsWith('//')) return fallback
  // eslint-disable-next-line no-control-regex
  if (/[\\\s\u0000-\u001f\u007f]/.test(raw)) return fallback
  let url: URL
  try {
    url = new URL(raw, SAME_ORIGIN_PROBE)
  } catch {
    return fallback
  }
  if (url.origin !== SAME_ORIGIN_PROBE) return fallback
  if (url.pathname === MFA_CHALLENGE_PATH || url.pathname.startsWith(`${MFA_CHALLENGE_PATH}/`)) return fallback
  const out = url.pathname + url.search + url.hash
  // The URL parser removes dot segments, so "/.//evil" or "/%2e//evil" normalises to "//evil" (protocol-relative):
  // check the NORMALISED result, not only the raw input.
  if (out.startsWith('//') || out.startsWith('/\\')) return fallback
  return out
}

export function challengePathFor(destination: string | null | undefined): string {
  return `${MFA_CHALLENGE_PATH}?returnTo=${encodeURIComponent(safeRelativeDestination(destination))}`
}

export function enrolmentPathFor(destination: string | null | undefined): string {
  return `${MFA_ENROL_PATH}?mfa=enroll&returnTo=${encodeURIComponent(safeRelativeDestination(destination))}#mfa`
}

export type MfaGateInput = {
  enforced: boolean
  /** true ONLY when GoTrue verified the session (getUser succeeded); never for the unverified-JWT fallback. */
  identityVerified: boolean
  role: string | null | undefined
  currentAal: Aal | null
  /** user.factors from the VERIFIED getUser() response. */
  factors: readonly FactorLike[] | null | undefined
  pathname: string
  search: string
}

export type MfaGateDecision =
  | { kind: 'allow' }
  | { kind: 'challenge'; location: string }
  | { kind: 'enroll'; location: string }

function onPath(pathname: string, base: string): boolean {
  const p = pathname === '/' ? '/' : pathname.replace(/\/$/, '')
  return p === base || p.startsWith(`${base}/`)
}

/** The server-side rule: when enforced, an officer-tier user must be at AAL2 to use the app. */
export function decideOfficerMfaGate(input: MfaGateInput): MfaGateDecision {
  if (!input.enforced) return { kind: 'allow' }
  if (!isMfaRequiredRole(input.role)) return { kind: 'allow' }
  // AAL2 counts only on a GoTrue-verified identity: a structurally parsed, unsigned JWT can claim anything.
  if (input.identityVerified && input.currentAal === 'aal2') return { kind: 'allow' }
  // The challenge page must be reachable at AAL1 (it also offers enrolment when no factor exists).
  if (onPath(input.pathname, MFA_CHALLENGE_PATH)) return { kind: 'allow' }

  const destination = input.pathname + (input.search || '')
  if (input.identityVerified && hasVerifiedTotpFactor(input.factors)) {
    return { kind: 'challenge', location: challengePathFor(destination) }
  }
  // No verified factor: send to enrolment rather than lock the officer out. The profile page is an allowed
  // AAL1 surface ONLY in this state; with a factor present it is not an escape hatch from the challenge.
  if (onPath(input.pathname, MFA_ENROL_PATH)) return { kind: 'allow' }
  return { kind: 'enroll', location: enrolmentPathFor(destination) }
}

/**
 * After password sign-in, keep the ordinary destination. The client cannot read the server-only enforcement
 * switch; sending factor holders to /mfa here would change sign-in while the switch is off. When enforcement is
 * on, the middleware checks the verified identity and AAL on the destination request and redirects officers.
 */
export function destinationAfterPasswordSignIn(returnTo: string | null | undefined): string {
  return safeRelativeDestination(returnTo)
}
